import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Label, TextInput, Button } from "flowbite-react";
import { HiEye, HiEyeOff, HiArrowLeft, HiCheckCircle } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { toast } from "@/lib/toast";
import { useForgotPasswordMutation, useResetPasswordMutation } from "@/api/auth";
import { getErrorMessage } from "@/utils/requestUtils";

const MIN_PASSWORD_LENGTH = 8;

/**
 * Two steps on one screen: ask for a code, then spend it.
 *
 * The email is carried into the second step rather than asked for twice, and
 * the first step reports the same thing whether or not the address has an
 * account — the server deliberately does not say, and repeating that here
 * would undo it.
 */
export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);

  const [step, setStep] = useState<"request" | "reset" | "done">("request");
  // Sign-in takes either, so this does too.
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(15);
  // Matches the server's cooldown: within it a resend is accepted but sends
  // nothing, so an enabled button would promise a mail that never arrives.
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const [forgotPassword, { isLoading: isRequesting }] = useForgotPasswordMutation();
  const [resetPassword, { isLoading: isResetting }] = useResetPasswordMutation();

  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!identifier.trim()) {
      setError(t("auth:forgotPassword.identifierRequired"));
      return;
    }
    try {
      const res = await forgotPassword({ email_or_username: identifier.trim() }).unwrap();
      setExpiresIn(res?.expires_in_minutes ?? 15);
      setCooldown(60);
      setStep("reset");
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code.trim())) {
      setError(t("auth:forgotPassword.codeInvalid"));
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(t("auth:forgotPassword.passwordTooShort", { count: MIN_PASSWORD_LENGTH }));
      return;
    }
    if (password !== confirm) {
      setError(t("auth:forgotPassword.passwordsDiffer"));
      return;
    }
    try {
      await resetPassword({
        email_or_username: identifier.trim(),
        reset_code: code.trim(),
        new_password: password,
      }).unwrap();
      setStep("done");
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  const resend = async () => {
    setError(null);
    try {
      await forgotPassword({ email_or_username: identifier.trim() }).unwrap();
      setCode("");
      setCooldown(60);
      toast.info(t("auth:forgotPassword.resent"));
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="mb-2 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <HiArrowLeft className="h-4 w-4" />
          {t("auth:forgotPassword.backToLogin")}
        </button>

        {step === "done" ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <HiCheckCircle className="h-12 w-12 text-green-500" />
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t("auth:forgotPassword.doneTitle")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-300">
              {t("auth:forgotPassword.doneBody")}
            </p>
            <Button color="primary" className="mt-2 w-full" onClick={() => navigate("/login")}>
              {t("auth:forgotPassword.goToLogin")}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t("auth:forgotPassword.title")}
            </h1>
            <p className="mb-4 text-sm text-gray-500 dark:text-gray-300">
              {step === "request"
                ? t("auth:forgotPassword.requestHint")
                : t("auth:forgotPassword.resetHint", { account: identifier, count: expiresIn })}
            </p>

            {step === "request" ? (
              <form onSubmit={handleRequest} className="flex flex-col gap-4">
                <div className="flex flex-col gap-y-2">
                  <Label htmlFor="identifier">
                    {t("auth:forgotPassword.identifier")}
                  </Label>
                  <TextInput
                    id="identifier"
                    type="text"
                    autoComplete="username"
                    autoFocus
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder={t("auth:forgotPassword.identifierPlaceholder")}
                  />
                </div>
                {error && <p className="text-sm text-red-500">{error}</p>}
                <Button type="submit" color="primary" disabled={isRequesting} isProcessing={isRequesting}>
                  {t("auth:forgotPassword.sendCode")}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleReset} className="flex flex-col gap-4">
                <div className="flex flex-col gap-y-2">
                  <Label htmlFor="code">{t("auth:forgotPassword.code")}</Label>
                  <TextInput
                    id="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                    className="[&_input]:tracking-[0.4em]"
                  />
                </div>

                <div className="flex flex-col gap-y-2">
                  <Label htmlFor="new-password">{t("auth:forgotPassword.newPassword")}</Label>
                  <div className="relative">
                    <TextInput
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                    >
                      {showPassword ? <HiEyeOff className="h-5 w-5" /> : <HiEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-y-2">
                  <Label htmlFor="confirm-password">{t("auth:forgotPassword.confirmPassword")}</Label>
                  <TextInput
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>

                {error && <p className="text-sm text-red-500">{error}</p>}

                <Button type="submit" color="primary" disabled={isResetting} isProcessing={isResetting}>
                  {t("auth:forgotPassword.submit")}
                </Button>

                <button
                  type="button"
                  onClick={resend}
                  disabled={isRequesting || cooldown > 0}
                  className="text-sm text-primary-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline dark:text-primary-300"
                >
                  {cooldown > 0
                    ? t("auth:forgotPassword.resendIn", { count: cooldown })
                    : t("auth:forgotPassword.resend")}
                </button>
              </form>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
